import { IMediaItem } from 'types/media'
import {
  OnDrag,
  OnDragEnd,
  OnResize,
  OnResizeEnd,
  OnResizeStart
} from 'react-moveable'
import { ECanvasObjectType } from '@renderforest/webwood-engine'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { useWebWoodContext } from 'context/WebWoodContext'
import { useTextSpecificActions } from './use-text-specific-actions'

export const useMovableActions = (item: IMediaItem) => {
  const engineId = `${item.id}-${item.assetId}`
  const { engine, updateObjectById, getMediaConfig } = useWebWoodContext()
  const {
    handleTextResizeStart,
    handleTextResize,
    handleTextResizeEnd,
    isText
  } = useTextSpecificActions(item)

  const handleDrag = ({ target, left: dragLeft, top: dragTop }: OnDrag) => {
    target.style.left = `${dragLeft}px`
    target.style.top = `${dragTop}px`
    updateObjectById(engineId, {
      x: dragLeft,
      y: dragTop
    })
  }

  const handleDragEnd = ({ lastEvent }: OnDragEnd) => {
    if (!lastEvent) return
    dispatch(
      projectDataThunks.updateMediaItem({
        id: item.id,
        updates: {
          coordinates: {
            x: lastEvent.left,
            y: lastEvent.top
          }
        }
      })
    )
  }

  const handleResizeStart = (e: OnResizeStart) => {
    if (isText) {
      handleTextResizeStart(e)
      // The engine's rotation-aware x/y compensation in
      // `BaseTextObject.renderAndApply` would shift x/y on every auto-render
      // fired during the drag (corner handle: fontSize changes; side handle:
      // text reflow). Moveable already owns x/y via `drag.left/top`, so the
      // engine's shift fights it. Suppress for the gesture lifetime.
      const editorText = engine?.getEditorText(engineId)
      if (editorText) editorText.suppressRotationCompensation = true
      // For side-handle (wrap) drags, switch the engine's live config into
      // fixed-width mode immediately. The engine's `renderAndApplyAtWidth`
      // also defensively forces `isAutoWidth=false` on render metadata, but
      // updating the live config keeps wrap-snapshot bookkeeping coherent
      // throughout the drag. Redux is committed at drag-end via the
      // existing `handleResizeEnd` updates payload.
      if (e.datas.isSideHandle) {
        updateObjectById(engineId, {
          metadata: { style: { isAutoWidth: false } }
        } as Record<string, unknown>)
      }
    }
  }

  const handleResize = ({ target, width, height, drag, datas }: OnResize) => {
    const isTextSideHandle = isText && datas.isSideHandle
    target.style.width = `${width}px`
    // For text side-handle drag the height is engine-derived from the wrap
    // result; let liveSize / styled-component drive it instead of locking it
    // to the (unchanged) moveable-reported height.
    if (!isTextSideHandle) {
      target.style.height = `${height}px`
    }
    target.style.left = `${drag.left}px`
    target.style.top = `${drag.top}px`

    if (isTextSideHandle) {
      // Side drag: only push position to engine. Width is delivered through
      // `EditorText.commitTextWrap(targetWidth)` which renders at the new
      // width AND updates config.width atomically when the bitmap is ready —
      // that's what gives live word re-flow without Phaser bitmap stretch.
      updateObjectById(engineId, { x: drag.left, y: drag.top })
      engine?.getEditorText(engineId)?.commitTextWrap(width)
      return
    }

    const engineUpdate: Record<string, unknown> = {
      x: drag.left,
      y: drag.top
    }

    if (isText) {
      handleTextResize(engineUpdate, datas, width)
    } else {
      engineUpdate.width = width
      engineUpdate.height = height
    }

    updateObjectById(engineId, engineUpdate)
  }

  const handleResizeEnd = ({
    target,
    lastEvent,
    datas,
    moveable
  }: OnResizeEnd) => {
    try {
      if (!lastEvent) return
      const { width: newWidth, height: newHeight, drag } = lastEvent
      const { left: newLeft, top: newTop } = drag

      const updates: Partial<IMediaItem> = {
        coordinates: {
          x: newLeft,
          y: newTop
        }
      }

      if (isText) {
        handleTextResizeEnd(updates, datas, newWidth)
        if (datas.isSideHandle) {
          // Final commit at the released width. The per-tick
          // `EditorText.commitTextWrap` calls in handleResize already drove
          // live re-flow; this last call ensures the worker renders the exact
          // released width (in case the pointer moved between the last
          // per-tick fire and `pointerup`).
          engine?.getEditorText(engineId)?.commitTextWrap(newWidth)
          const engineObject = getMediaConfig(ECanvasObjectType.text, engineId)
          updates.width = newWidth
          updates.height = engineObject?.height ?? newHeight
        } else {
          const engineObject = getMediaConfig(ECanvasObjectType.text, engineId)
          updates.width = engineObject?.width ?? newWidth
          updates.height = engineObject?.height ?? newHeight
        }
        target.style.width = `${updates.width}px`
        target.style.height = `${updates.height}px`
        moveable.updateRect()
      } else {
        updates.width = newWidth
        updates.height = newHeight
      }

      dispatch(
        projectDataThunks.updateMediaItem({
          id: item.id,
          updates
        })
      )
    } finally {
      // Re-enable rotation comp on EVERY exit path — including a zero-movement
      // resize (press + release with no drag) that early-returns above.
      // handleResizeStart suppressed it unconditionally, so it must be lifted
      // unconditionally here or the engine permanently stops recomputing x/y
      // for this object and later font/wrap changes drift it off its pivot.
      //
      // Runs synchronously right after the dispatch above, so the auto-render
      // the dispatch triggers (font-size change for corner, wrap-width for
      // side) still goes through `renderAndApply` — which IS allowed to
      // recompute x/y for rotated text now that Moveable is done.
      if (isText) {
        const editorText = engine?.getEditorText(engineId)
        if (editorText) editorText.suppressRotationCompensation = false
      }
    }
  }

  return {
    handleDrag,
    handleDragEnd,
    handleResizeStart,
    handleResize,
    handleResizeEnd
  }
}
